import { List, Set } from "immutable";
import { createSelector } from "reselect";

import compactDisplays from "../../../constants/compactDisplay";
import {
  ITEM_CARD_NARRATIVE_LIST_VIEW,
  ITEM_CARD_SEQUENCE_LIST_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../../constants/identifiers";
import itemTypeCategories from "../../../constants/itemTypeCategories";
import itemTypeIds from "../../../constants/itemTypeIds";
import { DEFAULT_DISPLAY_LABELS } from "../../../constants/styles/typeLabels";
import { immutableRecords } from "../../../types";
import { isDateValid } from "../calendar/dateFunctions";
import colorSelectors from "../colors/selectors";
import narrativeSettingsSelectors from "../narrativeSettings/selectors";
import referenceSelectors from "../references/selectors";
import definitionSelectors from "../selectors";
import timelineViewSettingsSelectors from "../timelineViewSettings/selectors";

const getTypeAllIds = state => {
  return definitionSelectors.getEnabledDefinitions(state).types.allIds;
};

const getTypeIsAllowedInTimeline = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId, "canHaveDate"]);

const getTypeIsDisplayedInTimeline = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId, "canHaveDate"]) &&
  definitionSelectors.getTimelineViewSettings(state).showTypes.contains(typeId);

const getTypeSetAllowedInTimeline = createSelector(
  // input
  state => definitionSelectors.getEnabledDefinitions(state).types.byId,
  // output
  typesById => {
    return typesById
      .filter(type => type.canHaveDate)
      .map(type => type.id)
      .toSet();
  }
);

const getTypeSetDisplayedInTimeline = createSelector(
  // input
  getTypeSetAllowedInTimeline,
  state => definitionSelectors.getTimelineViewSettings(state).showTypes,
  // output
  (allowedTypeIds, showTypeIds) => {
    return allowedTypeIds.filter(id => showTypeIds.contains(id));
  }
);

const getTypeIsAllowedInNarrative = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId, "canIncludeInNarrative"]);
const getTypeIsAllowedInSequence = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId, "showInSequence"]);

const getTypeCanHaveLocationUri = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId, "canHaveLocationUri"]);

const getTypeForId = (state, typeId) =>
  definitionSelectors.getEnabledDefinitions(state).getIn(["types", "byId", typeId]);

const getTypeValuesFromType = type => {
  return immutableRecords.ItemTypeDisplayValues({
    id: type.id,
    label: type.label,
    pluralLabel: type.pluralLabel,
    color: type.color,
    icon: type.icon,
    compactDisplay: type.compactDisplay
  });
};

const getTypesWithPropertyAccess = (newState, propertyId) => {
  const newTypesById = getTypesById(newState);
  return newTypesById
    .filter(newType => {
      return newType.properties.includes(propertyId);
    })
    .map((type, typeId) => typeId);
};

const getSuperTypes = (state, type) => {
  const t = getTypeForId(state, type);
  if (t !== undefined) {
    return t.superTypes;
  }
  return List();
};

const getTypeIdsHaveSuperType = (state, superTypeId) => {
  const typesById = getTypesById(state);
  return typesById
    .filter(type => type.superTypes.contains(superTypeId))
    .map(t => t.id)
    .toList();
};

// Get super types recursively
/**
 * @param {Map} typesById
 * @param {string} typeId
 */
const getAllSuperTypesFromTypeMap = createSelector(
  // input
  (typesById, _typeId) => typesById,
  (typesById, typeId) => typeId,
  // output
  (typesById, typeId) => {
    let result = Set();

    const getSuperTypesOfTypeId = typeId => {
      const superTypes = typesById.get(typeId).superTypes.filter(superTypeId => !result.contains(superTypeId));
      result = result.concat(superTypes);
      superTypes.forEach(superTypeId => getSuperTypesOfTypeId(superTypeId));
    };

    getSuperTypesOfTypeId(typeId);
    return result.toList();
  }
);

// Get super types recursively
const getAllSuperTypes = (state, typeId) => {
  let result = Set();

  const getSuperTypesOfTypeId = typeId => {
    const superTypes = getSuperTypes(state, typeId).filter(superTypeId => !result.contains(superTypeId));
    result = result.concat(superTypes);
    superTypes.forEach(superTypeId => getSuperTypesOfTypeId(superTypeId));
  };

  getSuperTypesOfTypeId(typeId);
  return result.toList();
};

// Get super types recursively
const getAllSuperTypesById = createSelector(
  // inputs
  state => getTypesById(state),
  // output
  typesById => typesById.map(type => getAllSuperTypesFromTypeMap(typesById, type.id))
);

// Get super types recursively
const getSuperTypesById = createSelector(
  // inputs
  state => getTypesById(state),
  // output
  typesById => typesById.map(type => type.superTypes)
);

const getAllSuperTypesOfTypes = (state, typeIds) => {
  let result = Set();
  typeIds.forEach(tId => {
    result = result.concat(getAllSuperTypes(state, tId));
  });
  return result.toList();
};

const canTypeHaveSuperType = (typesById, childType, superType) =>
  typesById.get(childType).superTypes.includes(superType);

const doesTypeHaveSuperType = (state, childType, superType) => {
  return getSuperTypes(state, childType).includes(superType);
};

const getTypeLabel = typeValues =>
  typeValues ? (typeValues.pluralLabel ? typeValues.pluralLabel : typeValues.label) : "";

const getTypeLabelDisplay = typeValues => {
  const label = typeValues ? (typeValues.labelDisplayLabel ? typeValues.labelDisplayLabel : "") : "";
  return label ? label : DEFAULT_DISPLAY_LABELS.LABEL;
};

const getTypeStartDisplay = typeValues => {
  const label = typeValues ? (typeValues.startDisplayLabel ? typeValues.startDisplayLabel : "") : "";
  return label ? label : DEFAULT_DISPLAY_LABELS.START_DATE;
};

const getTypeEndDisplay = typeValues => {
  const label = typeValues ? (typeValues.endDisplayLabel ? typeValues.endDisplayLabel : "") : "";
  return label ? label : DEFAULT_DISPLAY_LABELS.END_DATE;
};

const getTypeAnniversaryDisplay = typeValues => {
  const label = typeValues ? (typeValues.anniversaryDisplayLabel ? typeValues.anniversaryDisplayLabel : "") : "";
  return label ? label : DEFAULT_DISPLAY_LABELS.ANNIVERSARY;
};

const getDefaultItemColorByType = type => {
  return type.defaultItemColor || type.color;
};

const getDefaultItemSwatchColor = (state, typeId) => {
  // type color usually is "icon****"
  const typeColor = getDefaultItemColorByType(getTypeForId(state, typeId));
  const colorId = colorSelectors.getNearestColorIdByStateAndColorName(state, typeColor);
  return colorId || typeColor || definitionSelectors.getDefaultColorId(state);
};

const doesItemHaveRefWhileNewTypeDoesNotAccept = (item, newType, relationshipsById) => {
  return (
    item.references &&
    relationshipsById &&
    item.references.find(relId => {
      return !newType.references.contains(relationshipsById.get(relId).reference);
    })
  );
};

const doesItemHaveInverseRefWhileNewTypeDoesNotAccept = (item, newType, relationshipsById, referencesById) => {
  return (
    item.inverseReferences &&
    relationshipsById &&
    item.inverseReferences.find(relId => {
      return !referencesById.get(relationshipsById.get(relId).reference).types.contains(newType.id);
    })
  );
};

const doesItemHavePropertiesWhileNewTypeDoesNotAccept = (item, newType) => {
  return item.propertyValues.find((_prop, propId) => {
    return !newType.properties.contains(propId);
  });
};

const doesItemHaveSuperWhileNewTypeDoesNotAccept = (item, newType, itemsById) => {
  return item.super && !newType.superTypes.contains(itemsById.get(item.super).type);
};

const doesItemHaveChildrenWhileNewTypeDoesNotAcceptChildrenType = (item, newType, typesById, itemsById) => {
  return (
    item.children &&
    item.children.find(childId => {
      return !typesById.get(itemsById.get(childId).type).superTypes.contains(newType.id);
    })
  );
};

const doesItemHaveDateWhileNewTypeDoesNotAccept = (item, newType) => {
  return isDateValid(item.startDate) && !isDatedType(newType);
};

/**
 * If the new type can NOT accept the existed
 * 1. reference types
 * 2. inverse reference types
 * 3. super type
 * 4. children types
 * @param {string} newTypeId
 * @param {List|Array|Map} items A collection of item record with children (Eg. ItemDisplayRecord, InspectorItemRecord)
 * @param {Map<string, ItemRecord>} itemsById
 * @param {Map<string, TypeRecord>} typesById
 * @param {Map<string, ReferenceRecord>} referencesById
 * @param {Map<string, RelationshipRecord>} relationshipsById
 */
const doesNewTypeBreakItemValue = (newTypeId, items, itemsById, typesById, referencesById, relationshipsById) => {
  return (
    items.find(item => {
      const newType = typesById.get(newTypeId);
      return (
        item.type !== newTypeId &&
        // Is this redundant check with the following references and inverse references checks?
        (doesItemHaveRefWhileNewTypeDoesNotAccept(item, newType, relationshipsById) ||
          doesItemHaveInverseRefWhileNewTypeDoesNotAccept(item, newType, relationshipsById, referencesById) ||
          doesItemHavePropertiesWhileNewTypeDoesNotAccept(item, newType) ||
          doesItemHaveSuperWhileNewTypeDoesNotAccept(item, newType, itemsById) ||
          doesItemHaveChildrenWhileNewTypeDoesNotAcceptChildrenType(item, newType, typesById, itemsById) ||
          doesItemHaveDateWhileNewTypeDoesNotAccept(item, newType))
      );
    }) !== undefined
  );
};

const getOrderedTypes = createSelector(
  // Input functions
  getTypeAllIds,
  definitionSelectors.getTypesById,
  // Final function
  (allTypeIds, typesById) => {
    return allTypeIds.map(id => typesById.get(id));
  }
);

const getOrderedTypeIds = state => definitionSelectors.getEnabledDefinitions(state).types.allIds;

const getSidebarTypes = createSelector(
  // input
  state => getOrderedTypes(state),
  // output
  orderedTypes => orderedTypes.filter(t => isSidebarType(t))
);

const getSidebarTypeIds = state => getSidebarTypes(state).map(t => t.id);

const getShowAsColumnTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(t => t.showAsColumn)
);

const getShowAsColumnTypeIds = createSelector(
  // Input
  getShowAsColumnTypes,
  // Output
  showAsColumnTypes => getIdsFromTypesMap(showAsColumnTypes)
);

const getColumnIsVisibleTypes = createSelector(
  // input
  state => getOrderedTypes(state),
  // output
  orderedTypes => orderedTypes.filter(t => t.columnIsVisible)
);

const getTypeOptionFromType = type =>
  immutableRecords.DropdownOptionRecord({
    label: type.label,
    value: type.id,
    color: type.color,
    icon: type.icon
  });

const getTypeOptionsFromTypes = createSelector(
  // fetch parameters for last calculate function
  types => types,
  // Calculate result
  types => types.map(getTypeOptionFromType)
);

const getTypeOptions = state => getTypeOptionsFromTypes(getOrderedTypes(state));

const getAllIds = state => definitionSelectors.getEnabledDefinitions(state).types.allIds;

const getAllIdsLowerCase = createSelector(getAllIds, allIds => allIds.map(id => id.toLowerCase()));

/**
 * Try match the type id case-insensitive
 * @param {string} testTypeId
 * @param {List} allIds
 * @returns
 */
const matchTypeIdWithAllIds = (testTypeId, allIds) => {
  if (testTypeId) {
    const allIdsLowerCase = allIds.map(id => id.toLowerCase());
    const foundIndex = allIdsLowerCase.findIndex(id => id === testTypeId.toLowerCase());
    return foundIndex >= 0 ? allIds.get(foundIndex) : null;
  }
  return null;
};

/**
 * Try match the type id case-insensitive
 * @param {string} testTypeId
 * @param {List} allIds
 * @returns
 */
const matchTypeId = (testTypeId, state) => {
  let result = "";
  if (testTypeId) {
    const allIdsLowerCase = getAllIdsLowerCase(state);
    const allIds = getAllIds(state);
    const foundIndex = allIdsLowerCase.findIndex(id => id === testTypeId.toLowerCase());
    result = foundIndex >= 0 ? allIds.get(foundIndex) : "";
  }
  return result;
};

/**
 * Try match the type id case-insensitive
 * @param {string} testTypeId
 * @param {List} allIds
 * @returns
 */
const getMatchedType = (testTypeId, state) => getTypeForId(state, matchTypeId(testTypeId, state));

const getNarrativeFolderTypeId = state => {
  const t = getTypesById(state).find(t => isNarrativeFolderType(t));
  return t ? t.id : null;
};

const isDatedType = type => type.canHaveDate;
const isNarrativeType = type => type.canIncludeInNarrative;
const isNarrativeDocumentType = type => type.canIncludeInNarrative && !type.isNarrativeFolder;
const isNarrativeFolderType = type => type.canIncludeInNarrative && type.isNarrativeFolder;
const isSequenceType = type => type.showInSequence;
const isMindmapNode = type => type.id === itemTypeIds.MINDMAP_NODE_TYPE_ID;

const isAvatarType = type => type && type.compactDisplay === compactDisplays.AVATAR_INITIALS;
const isIdentifierType = type => type && type.compactDisplay === compactDisplays.IDENTIFIER;
const isShortLabelType = type => type && type.compactDisplay === compactDisplays.SHORT_LABEL;

const isPersonType = type => type.category === itemTypeCategories.PERSON;
const isLocationType = type => type.category === itemTypeCategories.LOCATION;
const isEventType = type => type.showInSequence;
const isOtherType = type => type.category === itemTypeCategories.OTHER && !type.showInSequence;
const isSidebarType = type => type.showOnSidebar;

const isNarrativeOnlyType = type =>
  !type.canHaveDate && !type.showInSequence && !type.showOnSidebar && type.canIncludeInNarrative;

const isMindmapOnlyType = (state, type) => {
  const sidebarTypes = getSidebarTypeIds(state);
  const allSuperTypesOfSidebarTypes = getAllSuperTypesOfTypes(state, sidebarTypes);
  return (
    !type.canHaveDate &&
    !type.showInSequence &&
    !type.showOnSidebar &&
    !type.canIncludeInNarrative &&
    !allSuperTypesOfSidebarTypes.includes(type.id)
  );
};

const getCanHaveDateTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(t => isDatedType(t))
);

const getTimelineTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  state => timelineViewSettingsSelectors.getTimelineViewSettings(state).showTypes,
  // Output
  (typesById, timelineShowTypes) => typesById.filter(t => isDatedType(t) && timelineShowTypes.includes(t.id))
);

const getNarrativeTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(isNarrativeType)
);

const getOrderedNarrativeTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isNarrativeType)
);

const getOrderedNarrativeDocumentTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isNarrativeDocumentType)
);

const getTypesAllowedNarrativeChildren = createSelector(
  // Input
  getOrderedNarrativeTypes,
  narrativeSettingsSelectors.getNarrativeUsesStrictFolderStructure,
  // Output
  (orderedTypes, useStrict) => orderedTypes.filter(t => !useStrict || t.isNarrativeFolder)
);

const getTypeIdsAllowedNarrativeChildren = createSelector(
  // Input
  getTypesAllowedNarrativeChildren,
  // Output
  orderedTypes => orderedTypes.map(t => t.id)
);

const getOrderedNoNarrativeOnlyTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filterNot(isNarrativeOnlyType)
);

const getSequenceTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(isSequenceType)
);

const getOrderedSequenceTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => {
    return orderedTypes.filter(isSequenceType);
  }
);

const getPersonTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(isPersonType)
);

const getLocationTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(isLocationType)
);

const getOtherTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(isOtherType)
);

const getCanHaveLocationUriTypes = createSelector(
  // Input
  definitionSelectors.getTypesById,
  // Output
  typesById => typesById.filter(type => type.canHaveLocationUri)
);

const getOrderedPersonTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isPersonType)
);

const getOrderedOtherTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isOtherType)
);

const isSyncToEntityType = t => !isSequenceType(t) && !isNarrativeType(t) && !isMindmapNode(t);

const getSyncToEntityTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isSyncToEntityType)
);

const isSyncToFolderType = t => !isNarrativeType(t) && !isMindmapNode(t);

const getSyncToFolderTypes = createSelector(
  // Input
  getOrderedTypes,
  // Output
  orderedTypes => orderedTypes.filter(isSyncToFolderType)
);

const getIdsFromTypesMap = types => types.map(t => t.id).toList();

const getCanHaveDateTypeIds = createSelector(
  // Input
  getCanHaveDateTypes,
  // Output
  getIdsFromTypesMap
);

const getTimelineTypeIds = createSelector(
  // Input
  getTimelineTypes,
  // Output
  getIdsFromTypesMap
);

const getNarrativeTypeIds = createSelector(
  // Input
  getNarrativeTypes,
  // Output
  getIdsFromTypesMap
);

const getPersonTypeIds = createSelector(
  // Input
  getPersonTypes,
  // Output
  getIdsFromTypesMap
);

const getLocationTypeIds = createSelector(
  // Input
  getLocationTypes,
  // Output
  getIdsFromTypesMap
);

const getOtherTypeIds = createSelector(
  // Input
  getOtherTypes,
  // Output
  getIdsFromTypesMap
);

const getSequenceTypeIds = createSelector(
  // Input
  getSequenceTypes,
  // Output
  getIdsFromTypesMap
);

const getCanHaveLocationUriTypeIds = createSelector(
  // Input
  getCanHaveLocationUriTypes,
  // Output
  getIdsFromTypesMap
);

const getEventTypeIds = getSequenceTypeIds;

const getTypeIdsForEntityList = createSelector(
  // Input
  getSidebarTypeIds,
  (state, viewType) => viewType,
  (state, viewType) => getSuperTypeValues(state, viewType),
  state => definitionSelectors.getEnabledDefinitions(state).types.allIds,
  // Output
  // (state, viewType) => {
  (sidebarTypeIds, viewType, superTypes, allTypeIds) => {
    return sidebarTypeIds.contains(viewType) ? List([viewType]).concat(superTypes.map(t => t.id)) : allTypeIds;
  }
);

// Note: In mobile mode, the view type of entity view
// is the same as the entity type
const getMobileTypeIdsByViewType = (state, viewType) => {
  switch (viewType) {
    case ITEM_CARD_SEQUENCE_LIST_VIEW:
      return getSequenceTypeIds(state);
    case ITEM_CARD_NARRATIVE_LIST_VIEW:
      return getNarrativeTypeIds(state);
    default:
      return getTypeIdsForEntityList(state, viewType);
  }
};

const getTypeIdsByViewType = (state, viewType) => {
  switch (viewType) {
    case TIMELINE_VIEW:
      return getTimelineTypeIds(state);
    case SUBWAY_VIEW:
    case RELATIONSHIP_VIEW:
    case SPREADSHEET_VIEW:
      return getSequenceTypeIds(state);
    case OUTLINE_VIEW:
    case NARRATIVE_VIEW:
      return getNarrativeTypeIds(state);
    default:
      return definitionSelectors.getEnabledDefinitions(state).types.allIds;
  }
};

const getTypesById = state => definitionSelectors.getTypesById(state);

const getItemTypeValuesForType = createSelector(
  // Input
  type => type,
  // Output
  type =>
    immutableRecords.ItemTypeDisplayValues({
      id: type.id,
      label: type.label,
      pluralLabel: type.pluralLabel,
      color: type.color,
      icon: type.icon,
      compactDisplay: type.compactDisplay
    })
);

const getItemTypeValuesForGuid = createSelector(
  // Input functions
  (state, typeId) => getTypeForId(state, typeId),
  // Result
  getItemTypeValuesForType
);

const getSuperTypeValues = createSelector(
  // Inputs
  (state, typeId) => getTypeForId(state, typeId),
  state => definitionSelectors.getEnabledDefinitions(state).types.byId,
  getAllSuperTypes,
  // Final function
  (type, typesById, allSuperTypes) =>
    type ? allSuperTypes.map(superId => getItemTypeValuesForType(typesById.get(superId))).toList() : List()
);

const getTypeIdsOfViewType = (state, viewType) => {
  let typeIds = List();
  switch (viewType) {
    case TIMELINE_VIEW:
      typeIds = getTimelineTypeIds(state);
      break;
    case NARRATIVE_VIEW:
      typeIds = getNarrativeTypeIds(state);
      break;
    default:
      break;
  }
  return typeIds;
};

const getReferenceTypesOfViewType = createSelector(
  getOrderedTypes,
  getTypeIdsOfViewType,
  referenceSelectors.getReferencesById,
  (_state, viewTypeId) => viewTypeId,
  (orderedTypes, typeIdsOfView, referenceById) =>
    orderedTypes.filter(
      t => typeIdsOfView.includes(t.id) || t.references.find(rId => referenceById.get(rId).types.includes(t.id))
    )
);

export default {
  getOrderedTypes,
  getOrderedTypeIds,
  getNarrativeFolderTypeId,
  getSidebarTypes,
  getShowAsColumnTypes,
  getShowAsColumnTypeIds,
  getColumnIsVisibleTypes,
  getTypeOptionsFromTypes,
  getTypeOptions,
  getTypeOptionFromType,
  getSidebarTypeIds,
  getTypesById,
  getTypeIdsByViewType,
  getTypeIdsForEntityList,
  getMobileTypeIdsByViewType,
  getTypeCanHaveLocationUri,
  getTypeForId,
  getTypeValuesFromType,
  getItemTypeValuesForGuid,
  isDatedType,
  isNarrativeType,
  isNarrativeDocumentType,
  isNarrativeFolderType,
  isSequenceType,
  isAvatarType,
  isIdentifierType,
  isShortLabelType,
  isPersonType,
  isLocationType,
  isEventType,
  isOtherType,
  isSidebarType,
  isMindmapNode,
  isNarrativeOnlyType,
  isMindmapOnlyType,
  getTypeIsAllowedInTimeline,
  getTypeIsDisplayedInTimeline,
  getTypeIsAllowedInNarrative,
  getTypeIsAllowedInSequence,
  getTypeSetAllowedInTimeline,
  getTypeSetDisplayedInTimeline,
  getCanHaveDateTypes,
  getTimelineTypes,
  getNarrativeTypes,
  getSequenceTypes,
  getOrderedSequenceTypes,
  getPersonTypes,
  getLocationTypes,
  getOtherTypes,
  getCanHaveDateTypeIds,
  getTimelineTypeIds,
  getNarrativeTypeIds,
  getOrderedNarrativeTypes,
  getOrderedNarrativeDocumentTypes,
  getOrderedNoNarrativeOnlyTypes,
  getTypesAllowedNarrativeChildren,
  getTypeIdsAllowedNarrativeChildren,
  getSequenceTypeIds,
  getPersonTypeIds,
  getLocationTypeIds,
  getEventTypeIds,
  getOtherTypeIds,
  getCanHaveLocationUriTypeIds,
  getOrderedPersonTypes,
  getOrderedOtherTypes,
  getSuperTypes,
  getSuperTypesById,
  getAllSuperTypes,
  getAllSuperTypesFromTypeMap,
  getAllSuperTypesOfTypes,
  getAllSuperTypesById,
  getTypeIdsHaveSuperType,
  getTypeLabel,
  getTypeLabelDisplay,
  getTypeStartDisplay,
  getTypeEndDisplay,
  getTypeAnniversaryDisplay,
  canTypeHaveSuperType,
  doesTypeHaveSuperType,
  doesNewTypeBreakItemValue,
  getTypesWithPropertyAccess,
  getAllIds,
  matchTypeIdWithAllIds,
  matchTypeId,
  getMatchedType,
  getSuperTypeValues,
  getDefaultItemColorByType,
  getDefaultItemSwatchColor,
  getReferenceTypesOfViewType,

  isSyncToEntityType,
  isSyncToFolderType,
  getSyncToEntityTypes,
  getSyncToFolderTypes
};
